/**
 * /producao/dbr — Indicadores PPCP / Drum-Buffer-Rope (Sprint P4).
 *
 * Tela analítica do loop bipagem → buffer → gargalo. Tudo medido pela
 * bipagem real (GET /api/producao/dbr) — métrica sem dado vem `null` do
 * backend e vira "—" com tooltip aqui, NUNCA número inventado (SEV-1).
 *
 * 3 zonas:
 *   Zona 1 — EntityHeader + seletor de janela (7d·30d·90d, persiste em
 *            localStorage `dbr.janela`) + ExportMenu + KpiStrip (5 cards;
 *            #3 rola até o Tambor, #5 até os críticos).
 *   Zona 2 — grid 60/40: bloco Tambor (utilização com meta 85%, 4
 *            mini-stats, máquinas, histórico de migrações) + WIP por
 *            etapa (13 barras, selo no gargalo, clique → Painel).
 *   Zona 3 — Throughput (mini-série recharts) · Corda · Pulmão (barra
 *            tricolor, zonas clicáveis) · Despacho do Tambor (fila
 *            ordenada por buffer + sinal de setup) · Críticos.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import EntityHeader from "../../components/EntityHeader";
import ExportMenu from "../../components/ExportMenu";
import KpiStrip from "../../components/KpiStrip";
import { useAuth } from "../../context/AuthContext";
import { formatarReais } from "../../services/financeiro";
import {
  BUFFER_ZONA_TONES,
  JANELAS_DBR,
  indicadoresDbr,
} from "../../services/producaoV2";
import {
  formatarDataCurta,
  formatarDataHoraCurta,
  formatarMinutosAtraso,
} from "../../utils/dataHora";
import { PageContainer } from "../../styles/shared";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SecondaryButton,
  SectionTitle,
} from "../Orcamentos/styles";
import { COR_CATEGORIA, LABEL_CATEGORIA } from "./categorias6m";
import { exportarDbr } from "./_dbr/exportarDbr";
import { DICA_SEM_PRECO, usePodeVerPrecos } from "./_shared/usePodeVerPrecos";
import {
  BarraFerramentas,
  LinhaErro,
  MiniStat,
  MiniStatGrid,
  Mono,
  NotaDiscreta,
  Shimmer,
  TabelaFixa,
  TituloBloco,
  fmtNum,
  fmtPct,
} from "./_shared/ui";

const CHAVE_JANELA = "dbr.janela";
const META_UTILIZACAO_PCT = 85;
const DICA_CARIMBO_FILA = "requer carimbo de fila (3º carimbo da bipagem)";

function lerJanelaInicial() {
  try {
    const salva = window.localStorage.getItem(CHAVE_JANELA);
    return JANELAS_DBR.includes(salva) ? salva : "30d";
  } catch {
    return "30d";
  }
}

/** Estado visual da utilização do Tambor (danger ≥95 · warning 80–95). */
function estadoUtilizacao(pct) {
  if (pct === null || pct === undefined) return "neutral";
  if (pct >= 95) return "danger";
  if (pct >= 80) return "warning";
  return "success";
}

const COR_SOLIDA = {
  danger: "var(--danger-solid)",
  warning: "var(--warning-solid)",
  success: "var(--success-solid)",
  neutral: "var(--neutral-solid)",
};
const COR_TEXTO = {
  danger: "var(--danger-text)",
  warning: "var(--warning-text)",
  success: "var(--success-text)",
  neutral: "var(--ink-3)",
};

/** Minutos decorridos desde um ISO datetime (null-safe). */
function minutosDesde(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, (Date.now() - t) / 60000);
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const GridZona2 = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 16px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const GridZona3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const LinhaTambor = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;

  strong {
    font-size: 16px;
    color: var(--ink);
  }
`;

const SubMono = styled.div`
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-3);
  margin-bottom: 12px;
`;

const BarraUtilizacao = styled.div`
  position: relative;
  height: 16px;
  border-radius: 999px;
  background: var(--surface-2);
  margin-bottom: 14px;

  .faixa {
    height: 100%;
    border-radius: 999px;
    transition: width 0.3s ease;
  }
  .meta {
    position: absolute;
    top: -3px;
    bottom: -3px;
    border-left: 2px dashed var(--ink-3);
  }
  .meta-lbl {
    position: absolute;
    top: -18px;
    transform: translateX(-50%);
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--ink-3);
    white-space: nowrap;
  }
`;

const LinhaWip = styled.button`
  display: grid;
  grid-template-columns: 34% 1fr 56px;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: none;
  padding: 4px 2px;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font: inherit;

  &:hover {
    background: var(--surface-hover);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  .rotulo {
    font-size: 12px;
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .trilho {
    height: 10px;
    border-radius: 999px;
    background: var(--surface-2);
    overflow: hidden;
  }
  .fatia {
    display: block;
    height: 100%;
    border-radius: 999px;
  }
  .valor {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-size: 12px;
    color: var(--ink-2);
    text-align: right;
  }
`;

const BarraPulmao = styled.div`
  display: flex;
  height: 18px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--surface-2);
  margin-bottom: 12px;

  span {
    display: block;
    height: 100%;
  }
`;

const ZonaPulmaoBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  border: none;
  background: none;
  font: inherit;
  font-size: 12px;
  color: var(--ink-2);
  padding: 5px 6px;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: var(--surface-hover);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  .dot {
    width: 9px;
    height: 9px;
    border-radius: 999px;
    flex-shrink: 0;
  }
  .nome {
    flex: 1;
  }
`;

const BotaoGhost = styled.button`
  border: none;
  background: none;
  color: var(--accent);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  white-space: nowrap;

  &:hover {
    background: var(--accent-soft);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const CabecalhoBloco = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const DotCausa = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  margin-right: 2px;
`;

const ValorGrande = styled.div`
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 22px;
  font-weight: 700;
  color: var(--ink);
`;

// ---------------------------------------------------------------------------
// Sub-blocos
// ---------------------------------------------------------------------------

/**
 * Chip discreto da origem da medição (Lote B — trade-off dos checkpoints
 * §2.5): com 2 checkpoints de bipagem, métricas de etapas sem checkpoint
 * caem no fallback pelos timestamps de apontamento manual. `bipagem` (ou
 * ausente) não ganha chip — é o caso nobre.
 */
function ChipOrigemMedicao({ origem, style }) {
  if (!origem || origem === "bipagem") return null;
  const rotulo = origem === "mista" ? "medição mista" : "por apontamento";
  return (
    <Pill
      $tone="neutral"
      style={style}
      title={
        origem === "mista"
          ? "Parte medida pela bipagem nos checkpoints, parte pelos timestamps de apontamento manual"
          : "Medido pelos timestamps de apontamento manual — menos preciso que a bipagem (adicione checkpoints na Config para medir no chão)"
      }
    >
      {rotulo}
    </Pill>
  );
}

function SeletorJanela({ janela, onChange }) {
  return (
    <div className="tabchip-group" role="tablist" aria-label="Janela de análise">
      {JANELAS_DBR.map((j) => (
        <button
          key={j}
          type="button"
          role="tab"
          aria-selected={janela === j}
          className={`tabchip${janela === j ? " active" : ""}`}
          style={{ border: "none", background: janela === j ? undefined : "transparent" }}
          onClick={() => onChange(j)}
        >
          {j}
        </button>
      ))}
    </div>
  );
}

function BlocoTambor({ tambor }) {
  const semGargalo = !tambor?.etapa;
  const estado = estadoUtilizacao(tambor?.utilizacao_pct);
  const util = tambor?.utilizacao_pct;

  return (
    <Card>
      <SectionTitle>Tambor (etapa-gargalo)</SectionTitle>
      {semGargalo ? (
        <EmptyMsg>
          Sem gargalo ativo — nenhum ambiente em produção nesta janela.
        </EmptyMsg>
      ) : (
        <>
          <LinhaTambor>
            <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
              Tambor atual:
            </span>
            <strong>{tambor.etapa.label}</strong>
            <Pill $tone={estado === "neutral" ? "neutral" : estado}>
              {util === null || util === undefined
                ? "utilização —"
                : `${fmtPct(util)} util.`}
            </Pill>
            <Pill $tone="neutral" title="Gargalo derivado da bipagem (não fixado por configuração)">
              {tambor.origem === "auto" ? "auto" : "fixado"}
            </Pill>
            <ChipOrigemMedicao origem={tambor.origem_medicao} />
          </LinhaTambor>
          <SubMono>
            {tambor.gargalo_desde
              ? `gargalo há ${tambor.gargalo_ha_dias_uteis ?? "—"} dia(s) útil(eis) · desde ${formatarDataCurta(tambor.gargalo_desde)}`
              : "sem bipagem suficiente na janela para datar o gargalo"}
          </SubMono>

          <BarraUtilizacao
            title={
              util === null || util === undefined
                ? "Utilização indisponível — sem bipagem de run/setup na janela"
                : `Utilização ${fmtPct(util)} · meta ${META_UTILIZACAO_PCT}%`
            }
          >
            <div
              className="faixa"
              style={{
                width: `${Math.min(Number(util) || 0, 100)}%`,
                background: COR_SOLIDA[estado],
              }}
            />
            <div className="meta" style={{ left: `${META_UTILIZACAO_PCT}%` }} />
            <div className="meta-lbl" style={{ left: `${META_UTILIZACAO_PCT}%` }}>
              meta {META_UTILIZACAO_PCT}%
            </div>
          </BarraUtilizacao>

          <MiniStatGrid style={{ marginBottom: 14 }}>
            <MiniStat>
              <div className="lbl">Ritmo</div>
              <div className="val">
                {tambor.ritmo_ambientes_dia === null
                  ? "—"
                  : `${fmtNum(tambor.ritmo_ambientes_dia, 2)} amb/dia`}
              </div>
              <div className="meta">
                {tambor.ritmo_alvo_ambientes_dia != null
                  ? `alvo ${fmtNum(tambor.ritmo_alvo_ambientes_dia, 2)}`
                  : "sem alvo configurado"}
              </div>
            </MiniStat>
            <MiniStat title={tambor.touch_time_medio_min === null ? DICA_CARIMBO_FILA : undefined}>
              <div className="lbl">Touch time médio</div>
              <div className="val">
                {tambor.touch_time_medio_min === null
                  ? "—"
                  : `${fmtNum(tambor.touch_time_medio_min)} min/peça`}
              </div>
              <div className="meta">separado de fila (D2)</div>
            </MiniStat>
            <MiniStat title={tambor.setup_medio_min === null ? DICA_CARIMBO_FILA : undefined}>
              <div className="lbl">Setup médio</div>
              <div className="val">
                {tambor.setup_medio_min === null
                  ? "—"
                  : `${fmtNum(tambor.setup_medio_min)} min`}
              </div>
              <div className="meta">separado do run (D2)</div>
            </MiniStat>
            <MiniStat
              title={
                tambor.pct_capacidade_retrabalho === null
                  ? "sem peça concluída no gargalo nesta janela"
                  : undefined
              }
            >
              <div className="lbl">Capacidade p/ retrabalho</div>
              <div
                className="val"
                style={
                  Number(tambor.pct_capacidade_retrabalho) > 10
                    ? { color: "var(--warning-text)" }
                    : undefined
                }
              >
                {fmtPct(tambor.pct_capacidade_retrabalho)}
              </div>
              <div className="meta">peças refeitas no gargalo</div>
            </MiniStat>
          </MiniStatGrid>

          <TituloBloco>Máquinas do gargalo</TituloBloco>
          {(tambor.maquinas || []).length === 0 ? (
            <NotaDiscreta style={{ margin: "0 0 14px" }}>
              Etapa manual/sem máquina — roteamento etapa→máquina entra na
              Config (P5).
            </NotaDiscreta>
          ) : (
            <NotaDiscreta style={{ margin: "0 0 14px" }}>
              {tambor.maquinas.length} máquina(s) roteada(s).
            </NotaDiscreta>
          )}
        </>
      )}

      <TituloBloco>Histórico do Tambor</TituloBloco>
      {(tambor?.historico || []).length === 0 ? (
        <EmptyMsg style={{ padding: 12 }}>
          Sem migrações de gargalo registradas na janela.
        </EmptyMsg>
      ) : (
        <TabelaFixa>
          <colgroup>
            <col style={{ width: "30%" }} />
            <col style={{ width: "26%" }} />
            <col style={{ width: "26%" }} />
            <col style={{ width: "18%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Etapa</th>
              <th>De</th>
              <th>Até</th>
              <th>Duração</th>
            </tr>
          </thead>
          <tbody>
            {tambor.historico.map((seg) => (
              <tr key={`${seg.etapa}-${seg.de}`}>
                <td title={seg.label}>{seg.label}</td>
                <td>
                  <Mono>{formatarDataCurta(seg.de)}</Mono>
                </td>
                <td>
                  <Mono>{seg.ate ? formatarDataCurta(seg.ate) : "atual"}</Mono>
                </td>
                <td>
                  <Mono>{seg.dias_uteis}d úteis</Mono>
                </td>
              </tr>
            ))}
          </tbody>
        </TabelaFixa>
      )}
      <NotaDiscreta>
        O gargalo migra — esta tela observa a mudança pela bipagem, não a
        fixa por configuração.
      </NotaDiscreta>
    </Card>
  );
}

function BlocoWip({ wip, onAbrirPainel }) {
  const porEtapa = wip?.por_etapa || [];
  const maxAmbientes = Math.max(...porEtapa.map((e) => e.ambientes), 1);

  return (
    <Card>
      <CabecalhoBloco>
        <SectionTitle>WIP por etapa</SectionTitle>
        <Mono style={{ fontSize: 12, color: "var(--ink-3)" }}>
          {wip?.total ?? 0} ambientes · {wip?.lotes_ativos ?? 0} lote(s)
        </Mono>
      </CabecalhoBloco>
      {porEtapa.map((etapa) => {
        const cor = etapa.eh_tambor
          ? "var(--danger-solid)"
          : etapa.ambientes > 0
            ? "var(--info-solid)"
            : "var(--surface-3)";
        return (
          <LinhaWip
            key={etapa.codigo}
            type="button"
            title={`${etapa.label} — ${etapa.ambientes} ambiente(s) · ${etapa.pecas_em_fila} peça(s) em fila. Abrir no Painel.`}
            onClick={onAbrirPainel}
          >
            <span className="rotulo">
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {etapa.label}
              </span>
              {etapa.eh_tambor && <Pill $tone="danger">Tambor</Pill>}
              <ChipOrigemMedicao origem={etapa.origem_medicao} />
            </span>
            <span className="trilho">
              <span
                className="fatia"
                style={{
                  width: `${(etapa.ambientes / maxAmbientes) * 100}%`,
                  background: cor,
                }}
              />
            </span>
            <span className="valor">{etapa.ambientes}</span>
          </LinhaWip>
        );
      })}
      <NotaDiscreta>
        Barra ∝ ambientes na etapa corrente · clique abre o Painel.
      </NotaDiscreta>
    </Card>
  );
}

function CardThroughput({ throughput, mostrarValores }) {
  const serie = (throughput?.serie || []).map((b) => ({
    ...b,
    rotulo: formatarDataCurta(b.inicio),
  }));
  const delta = throughput?.delta_valor_pct;

  return (
    <Card>
      <CabecalhoBloco>
        <SectionTitle>Throughput (janela)</SectionTitle>
        <ChipOrigemMedicao origem={throughput?.origem_medicao} />
      </CabecalhoBloco>
      {mostrarValores ? (
        <ValorGrande>{formatarReais(throughput?.valor_rs || 0)}</ValorGrande>
      ) : (
        // Gate de preço (regra da gestora): sem preco:view, o card conta
        // ambientes — o R$ (e o Δ derivado dele) não aparece.
        <ValorGrande title={DICA_SEM_PRECO}>
          {throughput?.ambientes ?? 0} ambientes carregados
        </ValorGrande>
      )}
      {mostrarValores && (
        <div style={{ fontSize: 12, marginBottom: 10 }}>
          {delta === null || delta === undefined ? (
            <span style={{ color: "var(--ink-4)" }} title="sem base de comparação na janela anterior">
              Δ — vs janela anterior
            </span>
          ) : (
            <span
              style={{
                color:
                  delta > 0
                    ? "var(--success-text)"
                    : delta < 0
                      ? "var(--danger-text)"
                      : "var(--ink-3)",
              }}
            >
              {delta > 0 ? "▲" : delta < 0 ? "▼" : "="} {fmtNum(Math.abs(delta))}%
              {" vs janela anterior"}
            </span>
          )}
        </div>
      )}
      {serie.length > 0 && (
        <ResponsiveContainer width="100%" height={110}>
          <BarChart data={serie} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <XAxis dataKey="rotulo" hide />
            <YAxis hide />
            <Tooltip
              formatter={(valor, nome) =>
                nome === "valor_rs" ? [formatarReais(valor), "Valor"] : [valor, "Ambientes"]
              }
              labelFormatter={(rotulo) => `Fechamento em ${rotulo}`}
            />
            <Bar
              dataKey={mostrarValores ? "valor_rs" : "ambientes"}
              fill="var(--chart-1)"
              radius={[3, 3, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
      <NotaDiscreta>
        {mostrarValores ? (
          <>
            <Mono>{throughput?.ambientes ?? 0}</Mono> ambientes carregados ·
            produção termina no carregamento (D10).
          </>
        ) : (
          <span title={DICA_SEM_PRECO}>
            produção termina no carregamento (D10) · valores em R$ requerem
            permissão de preços.
          </span>
        )}
      </NotaDiscreta>
    </Card>
  );
}

function CardCorda({ corda, onVerDespacho }) {
  const sinc = corda?.sincronia || {};
  return (
    <Card>
      <CabecalhoBloco>
        <SectionTitle>Corda (liberação)</SectionTitle>
        <ChipOrigemMedicao origem={corda?.origem_medicao} />
      </CabecalhoBloco>
      <MiniStatGrid style={{ gridTemplateColumns: "1fr 1fr" }}>
        <MiniStat>
          <div className="lbl">Liberadas hoje</div>
          <div className="val">{corda?.liberadas_hoje ?? 0}</div>
        </MiniStat>
        <MiniStat>
          <div className="lbl">Aguardando liberação</div>
          <div className="val">{corda?.aguardando_liberacao ?? 0}</div>
        </MiniStat>
        <MiniStat
          title={
            corda?.lead_time_corda_min === null
              ? "requer peça liberada + 1ª bipagem dentro da janela"
              : undefined
          }
        >
          <div className="lbl">Lead time da corda</div>
          <div className="val">
            {corda?.lead_time_corda_min === null || corda?.lead_time_corda_min === undefined
              ? "—"
              : formatarMinutosAtraso(corda.lead_time_corda_min, "0m")}
          </div>
          <div className="meta">liberação → 1ª bipagem</div>
        </MiniStat>
        <MiniStat>
          <div className="lbl">Sincronia</div>
          <div className="val" style={{ fontSize: 12 }}>
            {sinc.sincronizada === null || sinc.sincronizada === undefined ? (
              <Pill $tone="neutral" title="sem ritmo medido no Tambor — sem julgamento">
                —
              </Pill>
            ) : sinc.sincronizada ? (
              <Pill $tone="success">sincronizada</Pill>
            ) : (
              <Pill $tone="warning">acima do ritmo</Pill>
            )}
          </div>
          <div className="meta">
            <Mono>
              {fmtNum(sinc.liberadas_dia, 2)}/dia vs tambor{" "}
              {sinc.ritmo_tambor_dia === null || sinc.ritmo_tambor_dia === undefined
                ? "—"
                : fmtNum(sinc.ritmo_tambor_dia, 2)}
            </Mono>
          </div>
        </MiniStat>
      </MiniStatGrid>
      <NotaDiscreta>
        A corda solta trabalho no ritmo do gargalo — evita inflar o WIP. A
        Corda diz <em>quanto</em> entra;{" "}
        <BotaoGhost type="button" onClick={onVerDespacho} style={{ padding: "0 2px" }}>
          → ver ordem de despacho
        </BotaoGhost>{" "}
        diz <em>qual</em> entra primeiro.
      </NotaDiscreta>
    </Card>
  );
}

const ZONAS_PULMAO = [
  { id: "verde", nome: "Verde", cor: "var(--success-solid)" },
  { id: "amarelo", nome: "Amarelo", cor: "var(--warning-solid)" },
  { id: "vermelho", nome: "Vermelho", cor: "var(--danger-solid)" },
];

function CardPulmao({ pulmao, zonaAtiva, onZonaClick }) {
  const comBuffer = ZONAS_PULMAO.reduce(
    (soma, z) => soma + (pulmao?.[z.id]?.qtd || 0),
    0,
  );
  return (
    <Card>
      <SectionTitle>Pulmão (consumo de buffer)</SectionTitle>
      {comBuffer === 0 ? (
        <EmptyMsg style={{ padding: 12 }}>
          Nenhum ambiente com baseline de buffer no momento.
        </EmptyMsg>
      ) : (
        <BarraPulmao>
          {ZONAS_PULMAO.map((z) => (
            <span
              key={z.id}
              style={{
                width: `${pulmao[z.id].pct}%`,
                background: z.cor,
              }}
              title={`${z.nome}: ${pulmao[z.id].qtd} (${fmtPct(pulmao[z.id].pct)})`}
            />
          ))}
        </BarraPulmao>
      )}
      {ZONAS_PULMAO.map((z) => (
        <ZonaPulmaoBtn
          key={z.id}
          type="button"
          title={`Ver críticos filtrados pela zona ${z.nome.toLowerCase()}`}
          onClick={() => onZonaClick(z.id)}
          style={
            zonaAtiva === z.id
              ? { background: "var(--surface-active)" }
              : undefined
          }
        >
          <span className="dot" style={{ background: z.cor }} />
          <span className="nome">{z.nome}</span>
          <Mono>
            {pulmao?.[z.id]?.qtd ?? 0} · {fmtPct(pulmao?.[z.id]?.pct ?? 0)}
          </Mono>
        </ZonaPulmaoBtn>
      ))}
      <NotaDiscreta>
        Limiares: amarelo {fmtNum(pulmao?.limiares?.amarelo_pct ?? 33, 0)}% ·
        vermelho {fmtNum(pulmao?.limiares?.vermelho_pct ?? 66, 0)}% (Config).
        {Number(pulmao?.sem_buffer) > 0 && (
          <>
            {" "}
            <Mono>{pulmao.sem_buffer}</Mono> sem baseline de buffer.
          </>
        )}
      </NotaDiscreta>
    </Card>
  );
}

function ChipSetup({ sinal }) {
  if (!sinal) {
    return (
      <span style={{ color: "var(--ink-4)" }} title="sem atributo de cor/espessura no BOM da peça">
        —
      </span>
    );
  }
  if (sinal.troca) {
    return (
      <Pill $tone="warning" title={`Troca de setup para ${sinal.atributo}`}>
        ↻ troca p/ {sinal.atributo}
      </Pill>
    );
  }
  return (
    <Pill $tone="success" title={sinal.atributo}>
      mesma cor ✓
    </Pill>
  );
}

function BlocoDespacho({ despacho, onAbrirAmbiente, blocoRef }) {
  const etapaLabel = despacho?.etapa?.label;
  return (
    <Card ref={blocoRef}>
      <CabecalhoBloco>
        <SectionTitle>
          Despacho do Tambor{etapaLabel ? ` — ${etapaLabel}` : ""}
        </SectionTitle>
        <Pill $tone="info" title="Política de fila ativa (read-only — edição na Config)">
          Prioridade (consumo de buffer)
        </Pill>
      </CabecalhoBloco>
      <NotaDiscreta style={{ margin: "0 0 10px" }}>
        Ordenado por consumo de buffer (mais vermelho primeiro) · empate por
        prazo prometido.
      </NotaDiscreta>
      {!despacho?.etapa ? (
        <EmptyMsg>Sem gargalo ativo — não há fila de despacho.</EmptyMsg>
      ) : (despacho.fila || []).length === 0 ? (
        <EmptyMsg>
          Nenhum ambiente aguardando no Tambor — a {etapaLabel} está
          alimentada.
        </EmptyMsg>
      ) : (
        <>
          <TabelaFixa>
            <colgroup>
              <col style={{ width: "5%" }} />
              <col style={{ width: "24%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "10%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>#</th>
                <th>Ambiente</th>
                <th>Consumo buffer</th>
                <th>Peças</th>
                <th>Setup</th>
                <th>Aguardando há</th>
                <th>Próx. etapa</th>
                <th>Ação</th>
              </tr>
            </thead>
            <tbody>
              {despacho.fila.map((linha) => {
                const minutosFila = minutosDesde(linha.aguardando_desde);
                return (
                  <tr
                    key={linha.ambiente.id}
                    data-clicavel="true"
                    onClick={() => onAbrirAmbiente(linha.ambiente.id)}
                  >
                    <td>
                      <Mono>{linha.ordem}</Mono>
                    </td>
                    <td
                      title={`${linha.ambiente.nome} · ${linha.ambiente.pedido_numero} · ${linha.ambiente.cliente_nome || ""}`}
                    >
                      {linha.congelado && <Pill $tone="warning">❄</Pill>}{" "}
                      <strong>{linha.ambiente.nome}</strong>
                      <span style={{ color: "var(--ink-3)" }}>
                        {" "}
                        · <Mono>{linha.ambiente.pedido_numero}</Mono>
                        {linha.ambiente.cliente_nome
                          ? ` · ${linha.ambiente.cliente_nome}`
                          : ""}
                      </span>
                    </td>
                    <td>
                      <Pill $tone={BUFFER_ZONA_TONES[linha.consumo_buffer?.zona] || "neutral"}>
                        <Mono>{fmtPct(linha.consumo_buffer?.pct)}</Mono>
                      </Pill>
                    </td>
                    <td>
                      <Mono>{linha.pecas_no_posto}</Mono>
                    </td>
                    <td>
                      <ChipSetup sinal={linha.sinal_setup} />
                    </td>
                    <td
                      title={
                        linha.aguardando_desde
                          ? `na fila desde ${formatarDataHoraCurta(linha.aguardando_desde)}`
                          : "sem carimbo de entrada na fila"
                      }
                    >
                      <Mono>
                        {minutosFila === null
                          ? "—"
                          : formatarMinutosAtraso(minutosFila, "agora")}
                      </Mono>
                    </td>
                    <td title={linha.proxima_etapa?.label || ""}>
                      {linha.proxima_etapa ? (
                        <Pill $tone="neutral">{linha.proxima_etapa.label}</Pill>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAbrirAmbiente(linha.ambiente.id);
                        }}
                      >
                        Abrir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TabelaFixa>
          <NotaDiscreta>
            Agrupar mesma cor reduz setup — reordene se valer mais que o
            consumo de buffer (sinalização visual, não solver).
          </NotaDiscreta>
        </>
      )}
    </Card>
  );
}

function BlocoCriticos({
  criticos, zonaFiltro, onLimparZona, onAbrirAmbiente, onVerPainel, blocoRef,
}) {
  const visiveis = zonaFiltro
    ? criticos.filter((c) => c.consumo_buffer?.zona === zonaFiltro)
    : criticos;

  return (
    <Card ref={blocoRef}>
      <CabecalhoBloco>
        <SectionTitle>
          Ambientes críticos{" "}
          <Mono style={{ fontSize: 13, color: "var(--ink-3)" }}>
            ({visiveis.length})
          </Mono>
        </SectionTitle>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {zonaFiltro && (
            <BotaoGhost type="button" onClick={onLimparZona}>
              zona {zonaFiltro} — limpar filtro
            </BotaoGhost>
          )}
          <BotaoGhost type="button" onClick={onVerPainel}>
            ver todos os ambientes
          </BotaoGhost>
        </div>
      </CabecalhoBloco>
      {visiveis.length === 0 ? (
        <EmptyMsg>
          {zonaFiltro && zonaFiltro !== "vermelho"
            ? `Nenhum ambiente na zona ${zonaFiltro} entre os críticos — este bloco lista a zona vermelha.`
            : "Nenhum ambiente na zona vermelha — pulmões sob controle."}
        </EmptyMsg>
      ) : (
        <TabelaFixa>
          <colgroup>
            <col style={{ width: "11%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Pedido</th>
              <th>Ambiente</th>
              <th>Cliente</th>
              <th>Etapa atual</th>
              <th>Consumo buffer</th>
              <th>Atraso</th>
              <th>Causa</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((c) => (
              <tr
                key={c.id}
                data-clicavel="true"
                onClick={() => onAbrirAmbiente(c.id)}
                title={`${c.numero} · ${c.nome}`}
              >
                <td>
                  <Mono>{c.pedido_numero}</Mono>
                </td>
                <td title={`${c.numero} · ${c.nome}`}>
                  {c.congelado && <Pill $tone="warning">❄</Pill>}{" "}
                  <Mono>{c.numero}</Mono> · {c.nome}
                </td>
                <td title={c.cliente_nome || ""}>{c.cliente_nome || "—"}</td>
                <td title={c.etapa_atual?.label || ""}>
                  {c.etapa_atual ? (
                    <Pill $tone="neutral">{c.etapa_atual.label}</Pill>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  <Pill $tone={BUFFER_ZONA_TONES[c.consumo_buffer?.zona] || "neutral"}>
                    <Mono>{fmtPct(c.consumo_buffer?.pct)}</Mono>
                  </Pill>
                </td>
                <td>
                  <Mono
                    style={
                      Number(c.atraso_min) > 0
                        ? { color: "var(--danger-text)" }
                        : undefined
                    }
                  >
                    {formatarMinutosAtraso(c.atraso_min)}
                  </Mono>
                </td>
                <td title={LABEL_CATEGORIA[c.causa_dominante] || ""}>
                  {c.causa_dominante ? (
                    <Pill $tone="neutral">
                      <DotCausa
                        style={{ background: COR_CATEGORIA[c.causa_dominante] }}
                      />
                      {LABEL_CATEGORIA[c.causa_dominante] || c.causa_dominante}
                    </Pill>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TabelaFixa>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------

export default function Dbr() {
  const navigate = useNavigate();
  const { request } = useAuth();
  const podeVerPrecos = usePodeVerPrecos();

  const [janela, setJanela] = useState(lerJanelaInicial);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [zonaCriticos, setZonaCriticos] = useState(null);

  const refTambor = useRef(null);
  const refDespacho = useRef(null);
  const refCriticos = useRef(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(CHAVE_JANELA, janela);
    } catch {
      /* storage indisponível */
    }
  }, [janela]);

  const carregar = useCallback(async () => {
    setErro(null);
    setCarregando(true);
    try {
      setDados(await indicadoresDbr(request, { janela }));
    } catch (err) {
      setErro(err?.message || "Falha ao calcular indicadores PPCP.");
    } finally {
      setCarregando(false);
    }
  }, [request, janela]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const rolarAte = useCallback((ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const abrirAmbiente = useCallback(
    (id) => navigate(`/producao/ambientes/${id}`),
    [navigate],
  );

  // ---- KPI strip (5 cards; #3 e #5 rolam a tela — scrollIntoView) ----
  const itensKpi = useMemo(() => {
    if (!dados) return [];
    const t = dados.tambor || {};
    const th = dados.throughput || {};
    const delta = th.delta_valor_pct;
    const estadoUtil = estadoUtilizacao(t.utilizacao_pct);
    const criticosQtd = dados.pulmao?.vermelho?.qtd ?? 0;
    const ritmoAbaixoDoAlvo =
      t.ritmo_ambientes_dia != null &&
      t.ritmo_alvo_ambientes_dia != null &&
      t.ritmo_ambientes_dia < t.ritmo_alvo_ambientes_dia;

    return [
      // Gate de preço: sem preco:view o KPI vira contagem de ambientes
      // (o Δ deriva do R$ — some junto).
      !podeVerPrecos
        ? {
            label: "Throughput (janela)",
            value: (
              <span title={DICA_SEM_PRECO}>
                {`${th.ambientes ?? 0} ambientes`}
              </span>
            ),
            sub: <span title={DICA_SEM_PRECO}>carregados na janela</span>,
          }
        : {
            label: "Throughput (janela)",
            value: formatarReais(th.valor_rs || 0),
            sub:
              delta === null || delta === undefined ? (
                `${th.ambientes ?? 0} ambientes carregados`
              ) : (
                <span
                  style={{
                    color:
                      delta > 0
                        ? "var(--success-text)"
                        : delta < 0
                          ? "var(--danger-text)"
                          : "var(--ink-3)",
                  }}
                >
                  {delta > 0 ? "▲" : delta < 0 ? "▼" : "="} {fmtNum(Math.abs(delta))}% vs anterior
                </span>
              ),
          },
      {
        label: "WIP total",
        value: `${dados.wip?.total ?? 0} ambientes`,
        sub: `em ${dados.wip?.lotes_ativos ?? 0} lote(s)`,
      },
      {
        label: "Etapa-gargalo (Tambor)",
        value: t.etapa?.label || "—",
        sub: t.etapa ? (
          <span style={{ color: COR_TEXTO[estadoUtil] }}>
            {t.utilizacao_pct === null || t.utilizacao_pct === undefined
              ? "utilização — · clique para ver"
              : `${fmtPct(t.utilizacao_pct)} util. · clique para ver`}
          </span>
        ) : (
          "sem gargalo ativo na janela"
        ),
        onClick: () => rolarAte(refTambor),
      },
      {
        label: "Ritmo do Tambor",
        value:
          t.ritmo_ambientes_dia === null || t.ritmo_ambientes_dia === undefined
            ? "—"
            : `${fmtNum(t.ritmo_ambientes_dia, 2)} amb/dia`,
        sub: (
          <span style={ritmoAbaixoDoAlvo ? { color: "var(--warning-text)" } : undefined}>
            {t.ritmo_alvo_ambientes_dia != null
              ? `alvo ${fmtNum(t.ritmo_alvo_ambientes_dia, 2)}`
              : "sem alvo configurado"}
          </span>
        ),
      },
      {
        label: "Pulmão em risco",
        value: `${criticosQtd} crítico(s)`,
        sub: (
          <span
            style={{
              color:
                criticosQtd > 0 ? "var(--danger-text)" : "var(--success-text)",
            }}
          >
            {criticosQtd > 0
              ? "zona vermelha — clique para ver"
              : "nenhum na zona vermelha"}
          </span>
        ),
        onClick: () => rolarAte(refCriticos),
      },
    ];
  }, [dados, rolarAte, podeVerPrecos]);

  const aoClicarZona = (zona) => {
    setZonaCriticos((atual) => (atual === zona ? null : zona));
    rolarAte(refCriticos);
  };

  return (
    <PageContainer>
      <EntityHeader
        title="Indicadores PPCP (Drum-Buffer-Rope)"
        subtitle="Sincronização da produção pelo gargalo — bipagem nos checkpoints, apontamento manual nas demais etapas"
      />

      <BarraFerramentas>
        <SeletorJanela janela={janela} onChange={setJanela} />
        <ExportMenu
          disabled={!dados}
          onExport={(formato) =>
            dados
            && exportarDbr(formato, dados, { incluirValores: podeVerPrecos })
          }
        />
      </BarraFerramentas>

      {/* KPI strip nunca pisca números antigos durante o recálculo. */}
      {carregando ? <Shimmer $h={92} style={{ marginBottom: 16 }} /> : (
        dados && <KpiStrip items={itensKpi} />
      )}

      {erro && (
        <LinhaErro style={{ marginBottom: 14 }}>
          <ErrorMsg style={{ flex: 1 }}>
            Falha ao calcular indicadores PPCP. {erro}
          </ErrorMsg>
          <SecondaryButton type="button" onClick={carregar}>
            Tentar de novo
          </SecondaryButton>
        </LinhaErro>
      )}

      {carregando ? (
        <>
          <GridZona2 style={{ marginBottom: 16 }}>
            <Shimmer $h={320} />
            <Shimmer $h={320} />
          </GridZona2>
          <GridZona3>
            <Shimmer $h={200} />
            <Shimmer $h={200} />
            <Shimmer $h={200} />
          </GridZona3>
        </>
      ) : dados ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <GridZona2 ref={refTambor}>
            <BlocoTambor tambor={dados.tambor} />
            <BlocoWip
              wip={dados.wip}
              onAbrirPainel={() => navigate("/producao")}
            />
          </GridZona2>

          <GridZona3>
            <CardThroughput
              throughput={dados.throughput}
              mostrarValores={podeVerPrecos}
            />
            <CardCorda
              corda={dados.corda}
              onVerDespacho={() => rolarAte(refDespacho)}
            />
            <CardPulmao
              pulmao={dados.pulmao}
              zonaAtiva={zonaCriticos}
              onZonaClick={aoClicarZona}
            />
          </GridZona3>

          <BlocoDespacho
            despacho={dados.despacho}
            onAbrirAmbiente={abrirAmbiente}
            blocoRef={refDespacho}
          />

          <BlocoCriticos
            criticos={dados.criticos || []}
            zonaFiltro={zonaCriticos}
            onLimparZona={() => setZonaCriticos(null)}
            onAbrirAmbiente={abrirAmbiente}
            onVerPainel={() => navigate("/producao")}
            blocoRef={refCriticos}
          />
        </div>
      ) : null}
    </PageContainer>
  );
}
